import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  VideoLibrary as VideoIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";

export default function BannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Web, 1 = Mobile
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [bannerUrl, setBannerUrl] = useState("");
  const [isVideo, setIsVideo] = useState(false);
  
  // Mobile banner fields
  const [bannerData, setBannerData] = useState({
    logo: "",
    title: "",
    subtitle: "",
    badge: "",
    tagline: "",
    notes: "",
    buttonText: "",
    buttonAction: "",
    order: 0,
    isMobile: false,
    linkedArtistId: "",
    linkedPlaylistId: ""
  });

  // Search states
  const [artistSearch, setArtistSearch] = useState("");
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [searchResults, setSearchResults] = useState({ artists: [], playlists: [] });
  const [searching, setSearching] = useState(false);

  // Fetch existing banners
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await ApiService.GET_MEDIA_BANNERS();
      console.log("Banner response:", response);
      
      let bannersData = [];
      
      if (response.result) {
        // Handle different response structures
        if (response.result.data && Array.isArray(response.result.data)) {
          bannersData = response.result.data;
        } else if (Array.isArray(response.result)) {
          bannersData = response.result;
        } else if (response.result.uploadedAt && response.result.data) {
          // Single banner document with data array
          bannersData = response.result.data || [];
        }
      } else if (response.data) {
        bannersData = Array.isArray(response.data) ? response.data : [response.data];
      }
      
      console.log("Setting banners data:", bannersData);
      // Log each banner to check linkedArtistId
      bannersData.forEach((banner, index) => {
        console.log(`Banner ${index}:`, {
          uniqueId: banner.uniqueId,
          linkedArtistId: banner.linkedArtistId,
          linkedPlaylistId: banner.linkedPlaylistId,
          title: banner.title,
          isMobile: banner.isMobile
        });
      });
      setBanners(bannersData);
    } catch (error) {
      console.error("Error fetching banners:", error);
      notifier.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Create debounced search functions
  const debouncedSearchArtists = useCallback(
    debounce((query) => {
      searchArtists(query);
    }, 300),
    []
  );

  const debouncedSearchPlaylists = useCallback(
    debounce((query) => {
      searchPlaylists(query);
    }, 300),
    []
  );

  // Search for artists
  const searchArtists = async (query) => {
    if (!query.trim()) {
      setSearchResults(prev => ({ ...prev, artists: [] }));
      return;
    }
    
    setSearching(true);
    try {
      // Try the general search endpoint first - it includes all artists
      const searchResponse = await ApiService.SEARCH(query);
      
      if (searchResponse.result && searchResponse.result.artists) {
        // Extract artists from the general search results
        const artists = Array.isArray(searchResponse.result.artists) 
          ? searchResponse.result.artists.slice(0, 20) 
          : [];
        console.log('Search results artists:', artists);
        setSearchResults(prev => ({ ...prev, artists }));
      } else {
        // Fallback to artist-specific search
        const response = await ApiService.SEARCH_ARTIST(query);
        
        if (response.result) {
          const artists = Array.isArray(response.result) 
            ? response.result.slice(0, 20) 
            : [response.result];
          setSearchResults(prev => ({ ...prev, artists }));
        } else if (response.data) {
          const artists = Array.isArray(response.data) 
            ? response.data.slice(0, 20) 
            : [response.data];
          setSearchResults(prev => ({ ...prev, artists }));
        }
      }
    } catch (error) {
      console.error("Error searching artists:", error);
      // Final fallback to admin search
      try {
        const adminResponse = await ApiService.ADMIN_SEARCH_ARTIST({ query });
        if (adminResponse.result) {
          setSearchResults(prev => ({ ...prev, artists: adminResponse.result }));
        }
      } catch (adminError) {
        console.error("Admin search also failed:", adminError);
        notifier.error("Failed to search artists");
      }
    } finally {
      setSearching(false);
    }
  };

  // Search for playlists
  const searchPlaylists = async (query) => {
    if (!query.trim()) {
      setSearchResults(prev => ({ ...prev, playlists: [] }));
      return;
    }
    
    setSearching(true);
    try {
      const response = await ApiService.ADMIN_GET_ALL_PLAYLISTS({ query });
      if (response.result) {
        setSearchResults(prev => ({ ...prev, playlists: response.result }));
      }
    } catch (error) {
      console.error("Error searching playlists:", error);
      notifier.error("Failed to search playlists");
    } finally {
      setSearching(false);
    }
  };

  // Handle artist selection
  const selectArtist = (artist) => {
    console.log('Selected artist - full object:', artist);
    
    // Get artist ID - try multiple fields including uId
    const artistId = artist._id || artist.id || artist.userId || artist.uId;
    
    if (!artistId) {
      console.error('No artist ID found in:', artist);
      notifier.error('Error: Artist ID not found. Please try another artist or contact support.');
      return;
    }
    
    // Extract profile image URL from various possible fields
    let profileImageUrl = "";
    if (artist.profileImg?.filePath) {
      profileImageUrl = artist.profileImg.filePath;
    } else if (artist.profileImg && typeof artist.profileImg === 'string') {
      profileImageUrl = artist.profileImg;
    } else if (artist.profileImage) {
      profileImageUrl = artist.profileImage;
    } else if (artist.image) {
      profileImageUrl = artist.image;
    }
    
    const artistName = artist.displayArtistName || artist.displayName || `${artist.firstName || ''} ${artist.lastName || ''}`.trim() || artist.name || 'Artist';
    
    console.log('Setting banner data with artist ID:', artistId);
    
    setBannerData(prev => ({
      ...prev,
      linkedArtistId: artistId,
      logo: profileImageUrl,
      title: artistName,
      linkedPlaylistId: "" // Clear playlist selection
    }));
    setArtistSearch(artistName);
    setSearchResults(prev => ({ ...prev, artists: [] }));
    
    // Show success notification
    notifier.success(`Artist "${artistName}" selected (ID: ${artistId})`);
  };

  // Handle playlist selection
  const selectPlaylist = (playlist) => {
    const playlistName = playlist.title || playlist.name;
    
    setBannerData(prev => ({
      ...prev,
      linkedPlaylistId: playlist._id,
      logo: playlist.thumbnailImg?.filePath || "",
      title: playlistName,
      linkedArtistId: "" // Clear artist selection
    }));
    setPlaylistSearch(playlistName);
    setSearchResults(prev => ({ ...prev, playlists: [] }));
    
    // Show success notification
    notifier.success(`Playlist "${playlistName}" selected (ID: ${playlist._id})`);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileType = file.type;
      const isVideoFile = fileType.startsWith("video/");
      const isImageFile = fileType.startsWith("image/");

      if (!isVideoFile && !isImageFile) {
        notifier.error("Please select a valid image or video file");
        return;
      }

      setIsVideo(isVideoFile);
      setSelectedFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload/Update banner
  const handleUpload = async () => {
    // For new banners, require file selection
    if (!editingBanner && !selectedFile) {
      notifier.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      
      // Only append image if a new file is selected
      if (selectedFile) {
        formData.append("image", selectedFile);
      }
      
      // For editing, include the banner ID and existing image
      if (editingBanner) {
        const bannerId = editingBanner.uniqueId || editingBanner._id;
        console.log("Editing banner with ID:", bannerId, "Full banner object:", editingBanner);
        formData.append("bannerId", bannerId);
        
        // If no new file selected, pass the existing image
        if (!selectedFile && editingBanner.image) {
          formData.append("existingImage", JSON.stringify(editingBanner.image));
        }
      }
      
      // Set the URL - either custom URL or auto-generated based on linked content
      let finalUrl = bannerUrl;
      if (!finalUrl && (bannerData.linkedArtistId || bannerData.linkedPlaylistId)) {
        if (bannerData.linkedArtistId) {
          // Use correct artist profile page format
          finalUrl = `/app/ArtistProfile/${bannerData.linkedArtistId}`;
        } else if (bannerData.linkedPlaylistId) {
          // Use playlist page for playlists
          finalUrl = `/playlist/${bannerData.linkedPlaylistId}`;
        }
      }
      
      if (finalUrl) {
        formData.append("url", finalUrl);
      }
      
      // Append mobile banner fields
      console.log("Banner data before submission:", bannerData);
      Object.keys(bannerData).forEach(key => {
        if (bannerData[key] !== "") {
          formData.append(key, bannerData[key]);
          console.log(`Appending ${key}:`, bannerData[key]);
        }
      });

      // Use same endpoint for both create and update
      // The backend should handle updates based on the presence of bannerId
      const response = await ApiService.UPLOAD_BANNER(formData);
        
      if (response.success || response.result) {
        notifier.success(editingBanner ? "Banner updated successfully" : "Banner uploaded successfully");
        handleCloseDialog();
        fetchBanners();
      }
    } catch (error) {
      console.error(`Error ${editingBanner ? 'updating' : 'uploading'} banner:`, error);
      notifier.error(`Failed to ${editingBanner ? 'update' : 'upload'} banner`);
    } finally {
      setUploading(false);
    }
  };

  // Delete banner
  const handleDelete = async (bannerId) => {
    console.log("Attempting to delete banner with ID:", bannerId);
    
    if (!bannerId) {
      notifier.error("Invalid banner ID");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this banner?")) {
      return;
    }

    try {
      console.log("Sending DELETE request with payload:", { ids: [bannerId] });
      const response = await ApiService.DELETE_BANNERS({ ids: [bannerId] });
      console.log("Delete response:", response);
      
      if (response.status === 'success' || response.success || response.result) {
        notifier.success("Banner deleted successfully");
        // Force refresh after a small delay to ensure backend is updated
        setTimeout(() => {
          fetchBanners();
        }, 500);
      } else {
        console.error("Delete failed - no success/result in response:", response);
        notifier.error("Failed to delete banner - server response");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      notifier.error(`Failed to delete banner: ${error.response?.data?.message || error.message}`);
    }
  };

  // Toggle banner visibility
  const handleToggleVisibility = async (bannerId, currentVisibility) => {
    console.log("Toggling banner visibility:", { bannerId, currentVisibility });
    
    if (!bannerId) {
      notifier.error("Invalid banner ID");
      return;
    }

    const newVisibility = !currentVisibility;
    const action = newVisibility ? "show" : "hide";

    try {
      const response = await ApiService.TOGGLE_BANNER_VISIBILITY({
        ids: [bannerId],
        isVisible: newVisibility,
        adminId: "admin_user" // You might want to get this from user context
      });
      
      console.log("Toggle visibility response:", response);
      
      if (response.status === 'success' || response.success || response.result) {
        notifier.success(`Banner ${action === "show" ? "shown" : "hidden"} successfully`);
        // Refresh banners to show updated visibility status
        setTimeout(() => {
          fetchBanners();
        }, 500);
      } else {
        console.error("Toggle visibility failed:", response);
        notifier.error(`Failed to ${action} banner`);
      }
    } catch (error) {
      console.error("Error toggling banner visibility:", error);
      notifier.error(`Failed to ${action} banner: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
    setEditingBanner(null);
    setSelectedFile(null);
    setPreviewUrl("");
    setBannerUrl("");
    setIsVideo(false);
    setBannerData({
      logo: "",
      title: "",
      subtitle: "",
      badge: "",
      tagline: "",
      notes: "",
      buttonText: "",
      buttonAction: "",
      order: 0,
      isMobile: false,
      linkedArtistId: "",
      linkedPlaylistId: ""
    });
    setArtistSearch("");
    setPlaylistSearch("");
    setSearchResults({ artists: [], playlists: [] });
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    
    // Pre-fill the form with banner data
    setBannerData({
      logo: banner.logo || "",
      title: banner.title || "",
      subtitle: banner.subtitle || "",
      badge: banner.badge || "",
      tagline: banner.tagline || "",
      notes: banner.notes || "",
      buttonText: banner.buttonText || "",
      buttonAction: banner.buttonAction || "",
      order: banner.order || 0,
      isMobile: banner.isMobile || false,
      linkedArtistId: banner.linkedArtistId || "",
      linkedPlaylistId: banner.linkedPlaylistId || ""
    });
    
    // Set the banner URL if it exists
    if (banner.url) {
      setBannerUrl(banner.url);
    }
    
    // Set preview URL to current banner image
    if (banner.image?.filePath || banner.image) {
      const imageUrl = banner.image?.filePath || banner.image;
      setPreviewUrl(imageUrl);
      setIsVideo(imageUrl && (
        imageUrl.endsWith(".mp4") ||
        imageUrl.endsWith(".webm") ||
        imageUrl.endsWith(".mov")
      ));
    }
    
    setUploadDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
          Banner Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <IconButton
            onClick={fetchBanners}
            disabled={loading}
            sx={{
              color: "#667eea",
              border: "1px solid #667eea",
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.1)",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setBannerData(prev => ({ ...prev, isMobile: activeTab === 1 }));
              setUploadDialogOpen(true);
            }}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            Add New Banner
          </Button>
        </Box>
      </Box>

      {/* Tabs for Web and Mobile */}
      <Box sx={{ borderBottom: 1, borderColor: '#333', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#666',
              '&.Mui-selected': {
                color: '#667eea',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#667eea',
            },
          }}
        >
          <Tab label="Web Banners" />
          <Tab label="Mobile Banners" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (() => {
        // Filter banners based on active tab
        const filteredBanners = banners.filter(banner => {
          const isMobileBanner = banner.isMobile === true;
          return activeTab === 1 ? isMobileBanner : !isMobileBanner;
        });

        return filteredBanners.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: "center", bgcolor: "#1a1a1a" }}>
            <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
              No {activeTab === 1 ? 'mobile' : 'web'} banners uploaded yet
            </Typography>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => {
                setBannerData(prev => ({ ...prev, isMobile: activeTab === 1 }));
                setUploadDialogOpen(true);
              }}
              sx={{ color: "#667eea", borderColor: "#667eea" }}
            >
              Upload First {activeTab === 1 ? 'Mobile' : 'Web'} Banner
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredBanners.map((banner, index) => {
            const bannerId = banner.uniqueId || banner._id || index;
            return (
            <Grid item xs={12} md={6} lg={4} key={bannerId}>
              <Card sx={{ bgcolor: "#1a1a1a", height: "100%" }}>
                {(() => {
                  const imageUrl = banner.image?.filePath || banner.image;
                  const isVideo = imageUrl && (
                    imageUrl.endsWith(".mp4") ||
                    imageUrl.endsWith(".webm") ||
                    imageUrl.endsWith(".mov")
                  );
                  
                  return isVideo ? (
                    <CardMedia
                      component="video"
                      sx={{ height: 200 }}
                      src={imageUrl}
                      controls
                      muted
                    />
                  ) : (
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageUrl}
                      alt="Banner"
                      sx={{ objectFit: "cover" }}
                    />
                  );
                })()}
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Chip
                      label={banner.isVisible !== false ? "Visible" : "Hidden"}
                      size="small"
                      sx={{
                        bgcolor: banner.isVisible !== false ? "#4caf50" : "#ff9800",
                        color: "#fff",
                        fontSize: "0.7rem"
                      }}
                    />
                    {banner.isVisible === false && banner.hiddenAt && (
                      <Typography variant="caption" sx={{ color: "#ff9800", ml: 1 }}>
                        Hidden: {new Date(banner.hiddenAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                  {banner.url && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", wordBreak: "break-all" }}
                    >
                      Link: {banner.url}
                    </Typography>
                  )}
                  {banner.title && (
                    <Typography variant="body2" sx={{ color: "#888" }}>
                      Title: {banner.title}
                    </Typography>
                  )}
                  {banner.tagline && (
                    <Typography variant="body2" sx={{ color: "#888" }}>
                      Tagline: {banner.tagline}
                    </Typography>
                  )}
                  {banner.badge && (
                    <Typography variant="body2" sx={{ color: "#888" }}>
                      Badge: {banner.badge}
                    </Typography>
                  )}
                  {banner.isMobile && (
                    <Typography variant="body2" sx={{ color: "#667eea" }}>
                      Mobile Banner
                    </Typography>
                  )}
                  {banner.linkedArtistId && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 600 }}>
                        ✓ Linked to Artist
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#4caf50", display: 'block' }}>
                        Artist ID: {banner.linkedArtistId}
                      </Typography>
                    </Box>
                  )}
                  {banner.linkedPlaylistId && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 600 }}>
                        ✓ Linked to Playlist
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#4caf50", display: 'block' }}>
                        Playlist ID: {banner.linkedPlaylistId}
                      </Typography>
                    </Box>
                  )}
                  {!banner.linkedArtistId && !banner.linkedPlaylistId && (
                    <Typography variant="caption" sx={{ color: "#ff9800", display: 'block', mt: 1 }}>
                      ⚠️ No artist or playlist linked
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "#999", display: 'block', mt: 1 }}>
                    Banner ID: {banner.uniqueId || banner._id || "No ID"}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    onClick={() => handleEditBanner(banner)}
                    sx={{ color: "#667eea" }}
                    title="Edit Banner"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleToggleVisibility(bannerId, banner.isVisible !== false)}
                    sx={{ color: banner.isVisible !== false ? "#4caf50" : "#ff9800" }}
                    title={banner.isVisible !== false ? "Hide Banner" : "Show Banner"}
                  >
                    {banner.isVisible !== false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(bannerId)}
                    sx={{ color: "#f44336" }}
                    title="Delete Banner"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
        );
      })()}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBanner ? 'Edit Banner' : 'Upload New Banner'} 
          {!editingBanner && ` - ${activeTab === 1 ? 'Mobile' : 'Web'}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*,video/*"
              style={{ display: "none" }}
              id="banner-file-input"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="banner-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={selectedFile ? (isVideo ? <VideoIcon /> : <ImageIcon />) : <CloudUploadIcon />}
                sx={{ mb: 2, height: 56 }}
              >
                {selectedFile 
                  ? selectedFile.name 
                  : editingBanner 
                    ? "Change Image or Video (Optional)" 
                    : "Select Image or Video"
                }
              </Button>
            </label>

            {previewUrl && (
              <Box sx={{ mb: 2 }}>
                {isVideo ? (
                  <video
                    src={previewUrl}
                    controls
                    style={{ width: "100%", maxHeight: 300 }}
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{ width: "100%", maxHeight: 300, objectFit: "contain" }}
                  />
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label={
                bannerData.linkedArtistId || bannerData.linkedPlaylistId
                  ? "Banner Link URL (optional - will use artist/playlist page if empty)"
                  : "Banner Link URL (optional)"
              }
              value={bannerUrl}
              onChange={(e) => setBannerUrl(e.target.value)}
              placeholder={
                bannerData.linkedArtistId 
                  ? `Default: /app/ArtistProfile/${bannerData.linkedArtistId}`
                  : bannerData.linkedPlaylistId
                  ? `Default: /playlist/${bannerData.linkedPlaylistId}`
                  : "https://example.com"
              }
              helperText={
                bannerData.linkedArtistId || bannerData.linkedPlaylistId
                  ? "Leave empty to link to the selected artist/playlist page"
                  : ""
              }
              sx={{ mb: 2 }}
            />
            
            <Typography variant="h6" sx={{ mt: 3, mb: 2, color: "#667eea" }}>
              Link to Artist or Playlist
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Artists"
                  value={artistSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setArtistSearch(value);
                    debouncedSearchArtists(value);
                  }}
                  placeholder="Type artist name..."
                />
                {searchResults.artists.length > 0 && (
                  <Paper sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List>
                      {searchResults.artists.map((artist) => {
                        // Log artist data for debugging
                        console.log('Artist in search results:', {
                          _id: artist._id,
                          id: artist.id,
                          displayArtistName: artist.displayArtistName,
                          email: artist.email
                        });
                        
                        const artistId = artist._id || artist.id || artist.uId;
                        const artistKey = artistId || artist.email || Math.random();
                        
                        return (
                          <ListItem button key={artistKey} onClick={() => selectArtist(artist)}>
                            <ListItemAvatar>
                              <Avatar src={artist.profileImg?.filePath || artist.profileImg || artist.profileImage}>
                                {!artist.profileImg && (artist.displayArtistName?.[0] || artist.firstName?.[0] || 'A')}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={artist.displayArtistName || `${artist.firstName} ${artist.lastName}`}
                              secondary={
                                <span>
                                  {artist.email}
                                  <br />
                                  <small style={{ color: '#666' }}>ID: {artistId || 'NO ID FOUND'}</small>
                                </span>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Playlists"
                  value={playlistSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPlaylistSearch(value);
                    debouncedSearchPlaylists(value);
                  }}
                  placeholder="Type playlist name..."
                />
                {searchResults.playlists.length > 0 && (
                  <Paper sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List>
                      {searchResults.playlists.map((playlist) => (
                        <ListItem button key={playlist._id} onClick={() => selectPlaylist(playlist)}>
                          <ListItemAvatar>
                            <Avatar src={playlist.thumbnailImg?.filePath} />
                          </ListItemAvatar>
                          <ListItemText 
                            primary={playlist.title || playlist.name}
                            secondary={`${playlist.music?.length || playlist.songCount || 0} songs`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Grid>
            </Grid>

            {(bannerData.linkedArtistId || bannerData.linkedPlaylistId) && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(102, 126, 234, 0.1)', borderRadius: 2, border: '1px solid #667eea' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#667eea', fontWeight: 600 }}>
                  ✅ Selected {bannerData.linkedArtistId ? 'Artist' : 'Playlist'}:
                </Typography>
                <Chip 
                  label={bannerData.linkedArtistId ? `Artist: ${bannerData.title}` : `Playlist: ${bannerData.title}`}
                  onDelete={() => {
                    setBannerData(prev => ({ ...prev, linkedArtistId: "", linkedPlaylistId: "", logo: "", title: "" }));
                    setArtistSearch("");
                    setPlaylistSearch("");
                  }}
                  avatar={<Avatar src={bannerData.logo} />}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                    <strong>ID:</strong> {bannerData.linkedArtistId || bannerData.linkedPlaylistId}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                    <strong>Logo URL:</strong> {bannerData.logo ? `${bannerData.logo.substring(0, 50)}...` : 'No logo'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', mt: 1 }}>
                    ✓ This {bannerData.linkedArtistId ? 'artist' : 'playlist'} will be linked to the banner
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Typography variant="h6" sx={{ mt: 3, mb: 2, color: "#667eea" }}>
              Banner Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={bannerData.isMobile}
                  onChange={(e) => setBannerData({...bannerData, isMobile: e.target.checked})}
                  color="primary"
                  disabled={!editingBanner}
                />
              }
              label={editingBanner ? "Mobile Banner" : `${activeTab === 1 ? 'Mobile' : 'Web'} Banner (based on current tab)`}
              sx={{ mb: 2 }}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subtitle"
                  value={bannerData.subtitle}
                  onChange={(e) => setBannerData({...bannerData, subtitle: e.target.value})}
                  placeholder="Banner Subtitle"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tag"
                  value={bannerData.badge}
                  onChange={(e) => setBannerData({...bannerData, badge: e.target.value})}
                  placeholder="Tag text (e.g., NEW, FEATURED)"
                  helperText="This will appear as a green bordered badge on mobile"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Display Order"
                  value={bannerData.order}
                  onChange={(e) => setBannerData({...bannerData, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                  helperText="Lower numbers appear first"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              • Supported formats: JPG, PNG, GIF, MP4, WebM, MOV<br />
              • Maximum file size: 100MB<br />
              • Recommended dimensions: 1920x600 for desktop, 1080x1920 for mobile<br />
              • Link to artist/playlist to automatically set logo and title<br />
              • For mobile banners, only logo, subtitle, and tag are required
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={(!editingBanner && !selectedFile) || uploading}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            {uploading ? <CircularProgress size={24} /> : (editingBanner ? "Update" : "Upload")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}